import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from '../../../../../api/appPublicApi';

const API_ITEM_DETAIL = '/api/app/suggestedOrders/itemDetail';
const IMAGE_BASE_URL_SMALL = 'https://media.server.theshootingwarehouse.com/small/';
const IMAGE_BASE_URL_HIRES = 'https://media.server.theshootingwarehouse.com/hires/';

const RowDetailModal = ({ open, handleClose, itemNumber }) => {
  const theme = useTheme();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [hasImage, setHasImage] = useState(true);

  const handleInternalClose = useCallback(() => {
    setIsImageModalOpen(false);
    handleClose();

    setTimeout(() => {
      setProduct(null);
      setError(null);
      setIsLoading(false);
      setHasImage(true);
    }, 220);
  }, [handleClose]);

  useEffect(() => {
    if (!open || !itemNumber) return;

    const fetchDetails = async () => {
      setIsLoading(true);
      setError(null);
      setProduct(null);
      setHasImage(true);
      setIsImageModalOpen(false);

      try {
        const response = await axios.post(API_ITEM_DETAIL, { itemNumber });

        if (response.data && response.data.length > 0) {
          setProduct(response.data[0]);
        } else {
          setError(`No details found for item ${itemNumber}.`);
        }
      } catch (fetchError) {
        console.error('Failed to fetch product details:', fetchError);
        setError(fetchError.response?.data?.message || 'Failed to load product details.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [itemNumber, open]);

  const handleImageClick = useCallback(() => setIsImageModalOpen(true), []);
  const handleImageModalClose = useCallback(() => setIsImageModalOpen(false), []);

  const formatValue = useCallback((value) => {
    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : 'N/A';
    }
    if (value === undefined || value === null || value === '') {
      return 'N/A';
    }
    return value;
  }, []);

  const specificationEntries = useMemo(() => {
    if (!product?.facets) return [];

    return Object.entries(product.facets)
      .filter(([, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        return value !== undefined && value !== null && value !== '';
      })
      .map(([key, value]) => ({
        key,
        label: key
          .split('_')
          .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
          .join(' '),
        value: formatValue(value),
      }));
  }, [formatValue, product?.facets]);

  const metaDetails = useMemo(
    () => [
      { label: 'Item #', value: product?.itemno },
      { label: 'UPC #', value: product?.upc },
      { label: 'MFR #', value: product?.mfgino },
      { label: 'Department', value: product?.department },
      { label: 'Category', value: product?.category },
    ],
    [product],
  );

  const titleText = useMemo(() => {
    if (!product) return '';
    const headline = [product.brand_name, product.model].filter(Boolean).join(' ');
    if (headline) return headline;
    return product.description || product.item_description || `Item ${product.itemno ?? ''}`;
  }, [product]);

  const subtitleText = useMemo(() => {
    if (!product) return '';
    const parts = [product.series, product.category_group, product.department]
      .filter(Boolean)
      .map((part) => String(part).trim());
    return parts.join(' / ');
  }, [product]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleInternalClose}
        maxWidth='lg'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: 560,
            maxHeight: '90vh',
            overflow: 'hidden',
            backgroundImage: `linear-gradient(180deg, ${theme.palette.primary.main}08 0%, ${theme.palette.background.paper} 36%)`,
          },
        }}
      >
        <DialogTitle
          sx={{
            py: 1.75,
            px: 2.25,
            borderBottom: `1px solid ${theme.palette.primary.main}33`,
            backgroundColor: `${theme.palette.primary.main}14`,
            position: 'relative',
          }}
        >
          <Stack direction='row' alignItems='center' gap={2}>
            <Stack spacing={0.4} sx={{ minWidth: 0, pr: 4 }}>
              <Typography
                variant='h6'
                sx={{ fontWeight: 700, lineHeight: 1.25, color: theme.palette.primary.main }}
              >
                {titleText || 'Product Detail'}
              </Typography>
              {subtitleText ? (
                <Typography variant='body2' color='text.secondary'>
                  {subtitleText}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
          <IconButton
            onClick={handleInternalClose}
            aria-label='close'
            size='small'
            sx={{ position: 'absolute', top: 10, right: 10, p: 0.5 }}
          >
            <CloseIcon sx={{ color: theme.palette.delete.main, fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 0,
            borderColor: `${theme.palette.primary.main}22`,
            backgroundColor: `${theme.palette.primary.main}05`,
          }}
        >
          {isLoading ? (
            <Box sx={{ minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ p: 3 }}>
              <Alert severity='error'>{error}</Alert>
            </Box>
          ) : product ? (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '380px 1fr' },
                  gap: 2.5,
                  alignItems: 'start',
                }}
              >
                <Stack spacing={1.5}>
                  <Box
                    sx={{
                      position: 'relative',
                      height: { xs: 260, md: 300 },
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.primary.main}45`,
                      backgroundColor: theme.palette.background.paper,
                      boxShadow: `0 10px 24px ${theme.palette.primary.main}12`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      p: 1,
                    }}
                  >
                    {hasImage ? (
                      <Tooltip title='View larger image'>
                        <IconButton
                          size='small'
                          onClick={handleImageClick}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: theme.palette.primary.main,
                            backgroundColor: `${theme.palette.background.paper}e8`,
                            border: `1px solid ${theme.palette.primary.main}4d`,
                            '&:hover': { backgroundColor: theme.palette.background.paper },
                          }}
                        >
                          <ZoomInIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    {hasImage ? (
                      <Box
                        component='img'
                        src={`${IMAGE_BASE_URL_SMALL}${product.itemno}.jpg`}
                        alt={`${product.model || 'Product'} thumbnail`}
                        sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          setHasImage(false);
                        }}
                      />
                    ) : (
                      <Stack spacing={0.5} alignItems='center'>
                        <InfoOutlinedIcon color='disabled' />
                        <Typography variant='body2' color='text.secondary'>
                          Image not available
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                  <Box
                    sx={{
                      px: 1.25,
                      py: 1.1,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.primary.main}24`,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.background.paper} 55%)`,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                        columnGap: 2,
                        rowGap: 0.9,
                      }}
                    >
                      {metaDetails.map((detail) => (
                        <Box key={detail.label} sx={{ minWidth: 0 }}>
                          <Typography
                            variant='caption'
                            sx={{
                              display: 'block',
                              color: theme.palette.primary.main,
                              fontWeight: 700,
                              letterSpacing: '0.03em',
                              lineHeight: 1.1,
                            }}
                          >
                            {detail.label}
                          </Typography>
                          <Typography
                            variant='body2'
                            sx={{
                              mt: 0.15,
                              fontWeight: 600,
                              lineHeight: 1.2,
                              color: theme.palette.text.primary,
                              wordBreak: 'break-word',
                            }}
                          >
                            {formatValue(detail.value)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Stack>

                <Card
                  variant='outlined'
                  sx={{
                    borderColor: `${theme.palette.primary.main}3d`,
                    boxShadow: `0 8px 20px ${theme.palette.primary.main}10`,
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        backgroundColor: `${theme.palette.primary.main}12`,
                      }}
                    >
                      <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                        Specifications
                      </Typography>
                    </Box>
                    <Divider sx={{ borderColor: `${theme.palette.primary.main}2e` }} />
                    {specificationEntries.length > 0 ? (
                      <TableContainer sx={{ maxHeight: { xs: 360, md: 430 } }}>
                        <Table size='small' stickyHeader>
                          <TableBody>
                            {specificationEntries.map((entry, index) => (
                              <TableRow
                                key={entry.key}
                                sx={{
                                  backgroundColor:
                                    index % 2 === 0
                                      ? `${theme.palette.primary.main}08`
                                      : theme.palette.background.paper,
                                }}
                              >
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    width: '44%',
                                    color: theme.palette.primary.main,
                                  }}
                                >
                                  {entry.label}
                                </TableCell>
                                <TableCell>{entry.value}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ px: 2, py: 3 }}>
                        <Typography variant='body2' color='text.secondary'>
                          No additional specifications available.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography color='text.secondary'>Product details could not be loaded.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {product ? (
        <Dialog
          open={isImageModalOpen}
          onClose={handleImageModalClose}
          maxWidth='xl'
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle
            sx={{
              py: 1.25,
              px: 2,
              borderBottom: `1px solid ${theme.palette.primary.main}33`,
              backgroundColor: `${theme.palette.primary.main}10`,
              position: 'relative',
            }}
          >
            <Stack direction='row' alignItems='center' gap={1.5}>
              <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                {product.brand_name} {product.model} (Item: {product.itemno})
              </Typography>
            </Stack>
            <IconButton
              onClick={handleImageModalClose}
              aria-label='close image'
              size='small'
              sx={{ position: 'absolute', top: 10, right: 10, p: 0.5 }}
            >
              <CloseIcon sx={{ color: theme.palette.delete.main, fontSize: 18 }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box
              component='img'
              src={`${IMAGE_BASE_URL_HIRES}${product.itemno}.png`}
              alt={`${product.model || 'Product'} high-res`}
              sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
};

export default RowDetailModal;
